import React, { useState, useEffect } from "react";
import { Form, Typography, Button, Upload, Space, Input, Radio } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import styled from "styled-components";
import container from "container/BrokerDashboard/editAgentContainer";
import moment from "moment";
import { LoadingOutlined } from "@ant-design/icons";

const { Title } = Typography;
const { Dragger } = Upload;

// Function to extract agent ID from URL
const getAgentIdFromUrl = () => {
  const pathname = window.location.pathname;
  const match = pathname.match(/\/broker\/dashboard\/(\d+)\/orientation-info/);
  return match ? parseInt(match[1]) : null;
};

const SuccessMessage = styled.div`
  border-radius: 8px;
  background: var(--Success-600, #16a34a);
  position: fixed;
  right: 79.5px;
  top: 84px;
  display: flex;
  padding: 12px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  color: white;
  font-weight: 500;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const SectionContainer = styled.div`
  background: #f8f9fa;
  padding: 24px;
  border-radius: 12px;
  margin: 16px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 5px;
`;

const FormContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
`;

const SectionTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 0px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const DocBox = styled.div`
  padding: 16px;
  padding-top: 40px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  height: 185px;
  gap: 16px;
`;

const DocumentBox = styled.div`
  margin-top: 20px;
  padding: 24px;
  padding-top: 0px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
`;

const UploadGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 16px;
  margin-top: 24px;
`;

const StyledDragger = styled(Dragger)`
  background: #ffffff;
  padding: 16px;
  height: 100%;
  border-radius: 8px;

  .ant-upload-drag-icon {
    color: #1890ff;
  }

  .ant-upload-text {
    font-size: 14px;
    margin-bottom: 27px !important;
  }
`;

const BorderedBox = styled.div`
  background: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  padding: 20px;
  padding-bottom: 5px;
  margin-bottom: 10px;
  margin-top: 24px;
`;

const FormSection = styled.div`
  margin-bottom: 15px;
`;

const StyledFormItem = styled(Form.Item)`
  .ant-form-item-label > label {
    color: #262626;
    font-weight: 500;
    font-size: 14px;
  }

  .ant-input,
  .ant-picker {
    border: 1px solid #d9d9d9;
    border-radius: 8px !important;
    background: #ffffff;
    color: #262626;
    height: 40px;

    &:hover {
      border-color: #bfbfbf;
    }

    &:focus {
      border-color: #722ed1;
      box-shadow: 0 0 0 2px rgba(114, 46, 209, 0.1);
    }
  }

  .ant-input[disabled],
  .ant-picker-disabled {
    background: #ffffff;
    color: #262626;
    border-color: #d9d9d9;
  }

  .ant-picker {
    height: 40px !important;
    border-radius: 8px !important;

    .ant-picker-input > input {
      height: 38px;
      line-height: 38px;
      border-radius: 8px;
    }
  }

  .ant-picker-disabled {
    background: #ffffff !important;
    color: #262626 !important;
    border-color: #d9d9d9 !important;

    .ant-picker-input > input {
      background: #ffffff !important;
      color: #262626 !important;
    }

    .ant-picker-suffix {
      color: #bfbfbf !important;
    }
  }

  .ant-radio-group {
    .ant-radio-wrapper {
      color: #262626;
      font-size: 14px;

      .ant-radio {
        .ant-radio-inner {
          border-color: #d9d9d9;

          &:hover {
            border-color: #722ed1;
          }
        }

        &.ant-radio-checked .ant-radio-inner {
          border-color: #722ed1;

          &::after {
            background-color: #722ed1;
          }
        }
      }
    }
  }

  .ant-radio-group {
    .ant-radio-wrapper {
      color: #262626;
      font-size: 14px;

      .ant-radio {
        .ant-radio-inner {
          border-color: #d9d9d9;

          &:hover {
            border-color: #722ed1;
          }
        }

        &.ant-radio-checked .ant-radio-inner {
          border-color: #722ed1;

          &::after {
            background-color: #722ed1;
          }
        }
      }
    }
  }
`;

const DocumentDetails = ({
  getOrientationInfoResponse,
  updateOrientationInfo,
  getOrientationInfo,
  uploadDocsToBucket,
  setFileUploading,
  uploadDocsToBucketResponse,
}) => {
  const { data: orientationInfoResponse } = getOrientationInfoResponse || {};
  const [isEditable, setIsEditable] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form] = Form.useForm();
  const [successMessage, setSuccessMessage] = useState("");

  const [fileLists, setFileLists] = useState({
    licenseFile: [],
    w9File: [],
    sCorpW9File: [],
  });

  useEffect(() => {
    if (uploadDocsToBucketResponse?.data) {
      console.log("Upload Response:", uploadDocsToBucketResponse.data);
    }
  }, [uploadDocsToBucketResponse]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const renderUploadUI = (fieldName, title) => {
    const fileList = fileLists[fieldName] || [];

    if (fileList.length > 0 && fileList[0]?.url) {
      return (
        <DocBox style={{ position: "relative" }}>
          {isEditable && (
            <button
              onClick={() => handleRemoveDocument(fieldName)}
              style={{
                position: "absolute",
                top: "8px",
                right: "8px",
                width: "20px",
                height: "20px",
                backgroundColor: "white",
                color: "#ef4444",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "2px solid #ef4444",
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: "bold",
                zIndex: 10,
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
              title="Remove document"
            >
              ×
            </button>
          )}
          <div className="grid grid-rows-[auto_auto] place-items-center border border-dashed rounded-md p-6 bg-white text-center relative">
            <a
              href={fileList[0].url}
              target="_blank"
              rel="noopener noreferrer"
              className="cursor-pointer"
            >
              <img
                src="https://cdn-icons-png.flaticon.com/512/337/337946.png"
                alt="file"
                width={60}
                style={{ objectFit: "contain", marginBottom: "8px" }}
              />
            </a>
            <div className="text-base font-medium text-black text-center">
              {fileList[0].name}
            </div>
          </div>
        </DocBox>
      );
    }

    return (
      <StyledDragger
        name="file"
        multiple={false}
        fileList={fileList}
        showUploadList={true}
        disabled={!isEditable}
        customRequest={({ file, onSuccess }) => {
          const fileWithPreview = {
            uid: file.uid,
            name: file.name,
            status: "done",
            url: URL.createObjectURL(file),
            originFileObj: file,
          };

          const updatedList = [fileWithPreview];
          setFileLists((prev) => ({ ...prev, [fieldName]: updatedList }));
          form.setFieldsValue({ [fieldName]: updatedList });

          if (onSuccess) onSuccess("ok");
        }}
        onChange={(e) => {
          if (e.file.status === "uploading") {
            setFileUploading(fieldName);
            uploadDocsToBucket({
              file: e.file.originFileObj,
              objName: fieldName,
              fileName: e.file.name,
            });
          }
        }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined style={{ color: "#6366F1" }} />
        </p>
        <p className="ant-upload-text">Drag and drop a file to upload</p>
        <Button
          type="primary"
          style={{
            borderRadius: "8px",
            border: "1px solid var(--primary-500-main, #6366F1)",
            background: "var(--primary-500-main, #6366F1)",
            boxShadow:
              "0 1px 2px 0 rgba(16, 24, 40, 0.04), 0 1px 2px 0 rgba(16, 24, 40, 0.04)",
          }}
        >
          Browse Files
        </Button>
      </StyledDragger>
    );
  };

  const [removedDocuments, setRemovedDocuments] = useState({
    licenseFile: false,
    w9File: false,
    sCorpW9File: false,
  });

  const handleRemoveDocument = (fieldName) => {
    setFileLists((prev) => ({ ...prev, [fieldName]: [] }));
    form.setFieldsValue({ [fieldName]: [] });
    setRemovedDocuments((prev) => ({ ...prev, [fieldName]: true }));
  };

  useEffect(() => {
    const licenseFileList = orientationInfoResponse?.license_url
      ? [
          {
            uid: "-1",
            name: "License File",
            status: "done",
            url: orientationInfoResponse.license_url,
          },
        ]
      : [];

    const w9FileList = orientationInfoResponse?.w9_form_url
      ? [
          {
            uid: "-2",
            name: "W9 Form",
            status: "done",
            url: orientationInfoResponse.w9_form_url,
          },
        ]
      : [];

    const sCorpFileList = orientationInfoResponse?.s_corp_w9_form_url
      ? [
          {
            uid: "-3",
            name: "S Corp W9",
            status: "done",
            url: orientationInfoResponse.s_corp_w9_form_url,
          },
        ]
      : [];

    form.setFieldsValue({
      licenseFile: licenseFileList,
      w9File: w9FileList,
      sCorpW9File: sCorpFileList,
      // Add orientation fields
      legalAction:
        orientationInfoResponse?.legal_action_faced !== undefined
          ? Number(orientationInfoResponse.legal_action_faced)
          : "",
      reason: orientationInfoResponse?.reason_to_join_radius || "",
      referrer: orientationInfoResponse?.referred_by || "",
    });

    setFileLists({
      licenseFile: licenseFileList,
      w9File: w9FileList,
      sCorpW9File: sCorpFileList,
    });
  }, [orientationInfoResponse, form]);

  const handleCancel = () => {
    const licenseFileList = orientationInfoResponse?.license_url
      ? [
          {
            uid: "-1",
            name: "License File",
            status: "done",
            url: orientationInfoResponse.license_url,
          },
        ]
      : [];

    const w9FileList = orientationInfoResponse?.w9_form_url
      ? [
          {
            uid: "-2",
            name: "W9 Form",
            status: "done",
            url: orientationInfoResponse.w9_form_url,
          },
        ]
      : [];

    const sCorpFileList = orientationInfoResponse?.s_corp_w9_form_url
      ? [
          {
            uid: "-3",
            name: "S Corp W9",
            status: "done",
            url: orientationInfoResponse.s_corp_w9_form_url,
          },
        ]
      : [];

    form.setFieldsValue({
      licenseFile: licenseFileList,
      w9File: w9FileList,
      sCorpW9File: sCorpFileList,
      legalAction:
        orientationInfoResponse?.legal_action_faced !== undefined
          ? Number(orientationInfoResponse.legal_action_faced)
          : "",
      reason: orientationInfoResponse?.reason_to_join_radius || "",
      referrer: orientationInfoResponse?.referred_by || "",
    });

    setFileLists({
      licenseFile: licenseFileList,
      w9File: w9FileList,
      sCorpW9File: sCorpFileList,
    });

    setRemovedDocuments({
      licenseFile: false,
      w9File: false,
      sCorpW9File: false,
    });

    setIsEditable(false);
  };

  const handleToggle = async () => {
    if (isEditable) {
      try {
        setIsSaving(true);
        const values = await form.validateFields();
        const uploadData = uploadDocsToBucketResponse?.data || {};

        const payload = {
          agent_id: getAgentIdFromUrl(),
          license_url: removedDocuments.licenseFile
            ? null
            : uploadData.licenseFile?.licenseFile ||
              orientationInfoResponse?.license_url,
          w9_form_url: removedDocuments.w9File
            ? null
            : uploadData.w9File?.w9File || orientationInfoResponse?.w9_form_url,
          s_corp_w9_form_url: removedDocuments.sCorpW9File
            ? null
            : uploadData.sCorpW9File?.sCorpW9File ||
              orientationInfoResponse?.s_corp_w9_form_url,
          legal_action_faced: values.legalAction,
          reason_to_join_radius: values.reason,
          referred_by: values.referrer,
        };

        updateOrientationInfo({
          payload,
          callback: () => {
            getOrientationInfo({ agentId: getAgentIdFromUrl() });
            setIsEditable(false);
            setIsSaving(false);
            setSuccessMessage(
              "Document and orientation details updated successfully!"
            );
          },
        });
      } catch (errorInfo) {
        console.warn("Validation Failed:", errorInfo);
        setIsSaving(false);
      }
    } else {
      setIsEditable(true);
    }
  };

  return (
    <div>
      {successMessage && (
        <SuccessMessage>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13.3333 4L6 11.3333L2.66667 8"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Document and orientation details have been updated
        </SuccessMessage>
      )}
      <SectionHeader>
        <HeaderContent>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M6.75 9.75C6.75 9.33579 7.08579 9 7.5 9H16.5C16.9142 9 17.25 9.33579 17.25 9.75C17.25 10.1642 16.9142 10.5 16.5 10.5H7.5C7.08579 10.5 6.75 10.1642 6.75 9.75ZM7.5 13.5H16.5C16.9142 13.5 17.25 13.1642 17.25 12.75C17.25 12.3358 16.9142 12 16.5 12H7.5C7.08579 12 6.75 12.3358 6.75 12.75C6.75 13.1642 7.08579 13.5 7.5 13.5ZM21.75 5.25V19.5C21.7499 19.7599 21.6152 20.0012 21.394 20.1378C21.1729 20.2743 20.8968 20.2866 20.6644 20.1703L18 18.8381L15.3356 20.1703C15.1244 20.276 14.8756 20.276 14.6644 20.1703L12 18.8381L9.33562 20.1703C9.12436 20.276 8.87564 20.276 8.66438 20.1703L6 18.8381L3.33562 20.1703C3.1032 20.2866 2.82712 20.2743 2.60597 20.1378C2.38483 20.0012 2.25014 19.7599 2.25 19.5V5.25C2.25 4.42157 2.92157 3.75 3.75 3.75H20.25C21.0784 3.75 21.75 4.42157 21.75 5.25ZM20.25 5.25H3.75V18.2869L5.66437 17.3288C5.87564 17.223 6.12436 17.223 6.33563 17.3288L9 18.6619L11.6644 17.3288C11.8756 17.223 12.1244 17.223 12.3356 17.3288L15 18.6619L17.6644 17.3288C17.8756 17.223 18.1244 17.223 18.3356 17.3288L20.25 18.2869V5.25Z"
              fill="#111827"
            />
          </svg>
          <SectionTitle>Document Details</SectionTitle>
        </HeaderContent>
        {!isEditable ? (
          <Button
            type="default"
            style={{
              border: "1px solid #6366F1",
              borderRadius: "8px",
              background: "#6366F1",
              color: "white",
              fontWeight: 500,
              height: "36px",
              padding: "0 16px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
            onClick={handleToggle}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.41421 3.25253C6.19526 4.03358 6.19526 5.29991 5.41421 6.08096C4.63316 6.86201 3.36683 6.86201 2.58579 6.08096C1.80474 5.29991 1.80474 4.03358 2.58579 3.25253C3.36683 2.47149 4.63316 2.47149 5.41421 3.25253"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13.3333 4.66659H6"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13.4142 9.91904C14.1953 10.7001 14.1953 11.9664 13.4142 12.7475C12.6332 13.5285 11.3668 13.5285 10.5858 12.7475C9.80474 11.9664 9.80474 10.7001 10.5858 9.91904C11.3668 9.13799 12.6332 9.13799 13.4142 9.91904"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2.6665 11.3333H9.99984"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Edit
          </Button>
        ) : (
          <Space>
            <Button type="text" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              type="default"
              disabled={isSaving}
              style={{
                borderRadius: "8px",
                border: "1px solid #6366F1",
                background: "#6366F1",
                boxShadow:
                  "0 1px 2px 0 rgba(16, 24, 40, 0.04), 0 1px 2px 0 rgba(16, 24, 40, 0.04)",
                color: "white",
                fontWeight: 500,
                height: "36px",
                padding: "0 16px",
                display: "flex",
                alignItems: "center",
                gap: "12px", // Increased from 8px to 12px
              }}
              onClick={handleToggle}
            >
              {isSaving ? (
                <>
                  <LoadingOutlined style={{ color: "white", marginRight: "8px" }} />
                  Saving...
                </>
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16" 
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    style={{marginRight: "8px", position: "relative", top: "3px"}}
                  >
                    <path
                      d="M10.6663 12.6667H11.9997C13.4724 12.6667 14.6663 11.4728 14.6663 10V10C14.6663 8.52725 13.4724 7.33334 11.9997 7.33334V7.33334C11.9997 5.1242 10.2088 3.33334 7.99967 3.33334V3.33334C6.2878 3.33418 4.76634 4.42462 4.21554 6.04546C2.56745 6.26593 1.33598 7.67057 1.33301 9.33334H1.33301C1.33301 11.1743 2.82539 12.6667 4.66634 12.6667H5.33301"
                      stroke="white"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M9.33366 10.6667L8.00033 9.33334L6.66699 10.6667"
                      stroke="white"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M8.00033 12.6667V9.33334"
                      stroke="white"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  Save
                </>
              )}
            </Button>
          </Space>
        )}
      </SectionHeader>

      <Form layout="vertical" form={form}>
        <UploadGrid>
          <Form.Item name="licenseFile">
            <div>
              <Title level={5}>Real estate license</Title>
              {renderUploadUI("licenseFile", "Real estate license")}
            </div>
          </Form.Item>

          <Form.Item name="w9File">
            <div>
              <Title level={5}>W9 form</Title>
              {renderUploadUI("w9File", "W9 form")}
            </div>
          </Form.Item>

          <Form.Item name="sCorpW9File">
            <div>
              <Title level={5}>S Corp W9 Form</Title>
              {renderUploadUI("sCorpW9File", "S Corp W9 Form")}
            </div>
          </Form.Item>
        </UploadGrid>

        <FormSection>
          <BorderedBox>
            <StyledFormItem
              label="Have you had any legal action taken against you in the last 5 years?"
              name="legalAction"
            >
              <Radio.Group disabled={!isEditable}>
                <Radio value={1}>Yes</Radio>
                <Radio value={0}>No</Radio>
              </Radio.Group>
            </StyledFormItem>
            <StyledFormItem
              label="What is your main reason for joining Radius Agent?"
              name="reason"
            >
              <Input.TextArea
                rows={3}
                placeholder="Type your answer"
                readOnly={!isEditable}
                style={{ borderRadius: 8, minHeight: 100 }}
              />
            </StyledFormItem>
            <StyledFormItem label="Who referred you?" name="referrer">
              <Input placeholder="Enter referrer name" readOnly={!isEditable} />
            </StyledFormItem>
          </BorderedBox>
        </FormSection>
      </Form>
    </div>
  );
};

export default container(DocumentDetails);
